import { FC } from "react";
import { Box, Chip } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import { ClaimStatus } from "../../../../types/claims";
import { ObservabilityClaimStatus } from "../../../../types/observability";
import { PolicyStatus } from "../../../../types/chat";

type StatusChipConfig = {
  label: string;
  bgColor: string;
  textColor: string;
  dot?: "filled" | "outline";
  outlined?: boolean;
  borderColor?: string;
  borderStyle?: "solid" | "dashed";
};

const claimStatusConfig: Record<ClaimStatus, StatusChipConfig> = {
  [ClaimStatus.PAID]: {
    label: "Paid",
    bgColor: "#DCFCE7",
    textColor: "#166534",
    dot: "filled",
  },
  [ClaimStatus.DENIED]: {
    label: "Denied",
    bgColor: "#FEE2E2",
    textColor: "#991B1B",
  },
  [ClaimStatus.PARTIALLY_PAID]: {
    label: "Partially Paid",
    bgColor: "#DCFCE7",
    textColor: "#166534",
    dot: "outline",
  },
  [ClaimStatus.PENDING]: {
    label: "Pending",
    bgColor: "#F3F4F6",
    textColor: "#4B5563",
  },
  [ClaimStatus.PATIENT_RESPONSIBILITY]: {
    label: "Patient Resp.",
    bgColor: "#FFEDD5",
    textColor: "#9A3412",
  },
  [ClaimStatus.SECONDARY_DENIED]: {
    label: "2° Denied",
    bgColor: "#FEE2E2",
    textColor: "#991B1B",
  },
  [ClaimStatus.TERTIARY_DENIED]: {
    label: "3° Denied",
    bgColor: "#FEE2E2",
    textColor: "#991B1B",
  },
  [ClaimStatus.MISSING_CLAIM]: {
    label: "Missing Claim",
    bgColor: "transparent",
    textColor: "#64748B",
    outlined: true,
    borderColor: "#CBD5E1",
    borderStyle: "dashed",
  },
};

const observabilityStatusConfig: Record<
  ObservabilityClaimStatus,
  StatusChipConfig
> = {
  [ObservabilityClaimStatus.RUNNING]: {
    label: "Running",
    bgColor: "#DBEAFE",
    textColor: "#1E40AF",
  },
  [ObservabilityClaimStatus.COMPLETED]: {
    label: "Completed",
    bgColor: "#DCFCE7",
    textColor: "#166534",
  },
  [ObservabilityClaimStatus.FAILED]: {
    label: "Failed",
    bgColor: "#FEE2E2",
    textColor: "#991B1B",
  },
  [ObservabilityClaimStatus.TIMEOUT]: {
    label: "Timeout",
    bgColor: "#FFEDD5",
    textColor: "#9A3412",
  },
};

// Policy research table statuses (derived server-side from is_active and the effective/expiration dates).
const policyStatusConfig: Record<PolicyStatus, StatusChipConfig> = {
  active: {
    label: "Active",
    bgColor: "#DCFCE7",
    textColor: "#166534",
    dot: "filled",
  },
  upcoming: {
    label: "Upcoming",
    bgColor: "#DBEAFE",
    textColor: "#1E40AF",
  },
  expired: {
    label: "Expired",
    bgColor: "#F3F4F6",
    textColor: "#4B5563",
  },
};

const statusConfig: Record<string, StatusChipConfig> = {
  ...claimStatusConfig,
  ...observabilityStatusConfig,
  ...policyStatusConfig,
};

const unknownStatusConfig = (label: string): StatusChipConfig => ({
  label,
  bgColor: "#E5E7EB",
  textColor: "#374151",
});

interface StatusChipProps {
  status?: ClaimStatus | ObservabilityClaimStatus | PolicyStatus | string;
  /** Expands the chip to the full width of its label (and dot, if any) instead of letting a narrow container truncate it. */
  fitContent?: boolean;
  /** Forces a filled dot to be shown, even for statuses whose config doesn't define one. */
  dotted?: boolean;
  sx?: SxProps<Theme>;
}

const StatusChip: FC<StatusChipProps> = ({
  status,
  fitContent = false,
  dotted = false,
  sx,
}) => {
  const config =
    status !== undefined && status !== ""
      ? (statusConfig[status] ?? unknownStatusConfig(status))
      : unknownStatusConfig("—");

  const dot = config.dot ?? (dotted ? "filled" : undefined);

  const label = dot ? (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: "5px", minWidth: 0, maxWidth: "100%" }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: dot === "filled" ? config.textColor : "transparent",
          border:
            dot === "outline" ? `1.5px solid ${config.textColor}` : "none",
        }}
      />
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {config.label}
      </Box>
    </Box>
  ) : (
    config.label
  );

  return (
    <Chip
      label={label}
      size="small"
      sx={[
        {
          backgroundColor: config.bgColor,
          height: { xs: "20px", sm: "22px", md: "22px", lg: "24px" },
          borderRadius: "4px",
          border: config.outlined
            ? `1px ${config.borderStyle ?? "solid"} ${config.borderColor}`
            : "none",
          minWidth: 0,
          ...(fitContent && {
            width: "fit-content",
            maxWidth: "none",
            flexShrink: 0,
          }),
          "& .MuiChip-label": {
            minWidth: 0,
            color: config.textColor,
            fontWeight: 500,
            fontSize: {
              xs: "0.65rem",
              sm: "0.7rem",
              md: "0.75rem",
              lg: "0.875rem",
              xl: "0.875rem",
            },
            ...(fitContent && {
              overflow: "visible",
              textOverflow: "unset",
              whiteSpace: "nowrap",
            }),
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

export default StatusChip;
